import { useEffect } from 'react';
import { shouldOutputPreviewAudio } from '../engine/audioGate';
import { getPreviewAudioEngine } from '../engine/previewAudio';
import { useEditorStore } from '../state/editorStore';

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  if (tag === 'TEXTAREA') return true;
  if (tag === 'INPUT') {
    const type = (el as HTMLInputElement).type;
    // Numeric / control inputs should not swallow transport shortcuts
    if (
      type === 'number' ||
      type === 'range' ||
      type === 'checkbox' ||
      type === 'radio' ||
      type === 'button' ||
      type === 'submit' ||
      type === 'color' ||
      type === 'file'
    ) {
      return false;
    }
    // Slider value fields use type=text + inputMode=decimal
    const mode = (el as HTMLInputElement).inputMode;
    if (mode === 'decimal' || mode === 'numeric') return false;
    return true;
  }
  return false;
}

export function useKeyboardShortcuts() {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const s = useEditorStore.getState();
      const mod = e.metaKey || e.ctrlKey;

      // Space always toggles play/pause unless the user is typing prose
      if (e.key === ' ' || e.code === 'Space') {
        if (isTypingTarget(e.target)) return;
        e.preventDefault();
        e.stopPropagation();
        const next = !s.isPlaying;
        s.togglePlay();
        getPreviewAudioEngine().sync(
          s.project,
          s.playhead,
          next && shouldOutputPreviewAudio(s)
        );
        return;
      }

      if (isTypingTarget(e.target)) return;

      if (mod && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        if (e.shiftKey) s.redo();
        else s.undo();
        return;
      }
      if (mod && (e.key === 'y' || e.key === 'Y')) {
        e.preventDefault();
        s.redo();
        return;
      }

      switch (e.key) {
        case 'b':
        case 'B':
          if (!mod) {
            s.setTool(s.tool === 'blade' ? 'select' : 'blade');
          }
          break;
        case 'v':
        case 'V':
          if (!mod) s.setTool('select');
          break;
        case 't':
        case 'T':
          if (!mod) s.setTool('text');
          break;
        case 'Delete':
        case 'Backspace':
          e.preventDefault();
          if (s.selectedKeyframeIds.length) {
            s.deleteSelectedKeyframes();
          } else if (s.selectedGap) {
            s.deleteSelectedGap();
          } else if (e.shiftKey) {
            s.liftDeleteSelected();
          } else {
            s.rippleDeleteSelected();
          }
          break;
        case 'c':
        case 'C':
          if (!mod) s.cutAtPlayhead();
          break;
        case 'i':
        case 'I':
          s.setInPoint(s.playhead);
          break;
        case 'o':
        case 'O':
          s.setOutPoint(s.playhead);
          break;
        case '=':
        case '+':
          s.setTimelineZoom(s.timelineZoom * 1.15);
          break;
        case '-':
        case '_':
          s.setTimelineZoom(s.timelineZoom / 1.15);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          s.setPlayhead(s.playhead - (e.shiftKey ? 1 : 1 / s.project.settings.frameRate));
          break;
        case 'ArrowRight':
          e.preventDefault();
          s.setPlayhead(s.playhead + (e.shiftKey ? 1 : 1 / s.project.settings.frameRate));
          break;
        case 'Home':
          s.setPlayhead(0);
          break;
        case 's':
        case 'S':
          if (!mod) s.setSnapEnabled(!s.snapEnabled);
          break;
        case 'm':
        case 'M':
          if (!mod) {
            const markers = s.project.markers ?? [];
            const fps = s.project.settings.frameRate;
            const at = markers.find((m) => Math.abs(m.time - s.playhead) < 1 / fps / 2);
            if (at) s.removeMarker(at.id);
            else s.addMarker();
          }
          break;
        case 'f':
        case 'F': {
          if (mod) break;
          e.preventDefault();
          const viewer = document.getElementById('cinegrid-viewer');
          if (!viewer) break;
          if (document.fullscreenElement) {
            void document.exitFullscreen();
          } else {
            void viewer.requestFullscreen().catch(() => undefined);
          }
          break;
        }
        case 'Escape':
          if (document.fullscreenElement) {
            e.preventDefault();
            void document.exitFullscreen();
          }
          break;
        default:
          break;
      }
    };
    // Capture so Space works even when a panel button / control is focused
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);
}
